import * as yup from 'yup';
import { FormManager } from 'react-form-state-manager';
import { ReactNode, useState } from 'react';
import { startOfDay } from 'date-fns';
import { useTranslation } from 'react-i18next';

import { ErrorBag } from '../../common/helpers';
import { GridProps } from '../../theme/mixins';
import UI from '../../common/UI';
import useLocale from '../../common/useLocale';

interface DateRangeFormProps {
  form: FormManager;
  errors?: ErrorBag;
  requireStart?: boolean;
  requireEnd?: boolean;
  allowFullDay?: boolean;
  keys?: {
    startDate: string;
    startTime: string; // Only used for touch events
    endDate: string;
    endTime: string; // Only used for touch events
  };
  labels?: {
    startDate?: ReactNode;
    startTime?: ReactNode;
    endDate?: ReactNode;
    endTime?: ReactNode;
  };
  placeholders?: {
    startDate?: string;
    startTime?: string;
    endDate?: string;
    endTime?: string;
  };
}

const dateRangeKeys = {
  startDate: 'start_date',
  startTime: 'start_time',
  endDate: 'end_date',
  endTime: 'end_time',
};

/**
 * Show a date range selection and call the onChange callback when the selection changes.
 * Start and end dates are exchanged as UTC date time strings.
 */
const DateRangeForm = ({
  form, errors = {}, requireStart = false, requireEnd = false, allowFullDay = false, keys = dateRangeKeys, labels,
  placeholders,
}: DateRangeFormProps) => {
  const { t } = useTranslation();
  const { formatDate, parseDate } = useLocale();

  // UTC date is converted to project timezone for the date picker
  const [startDate, setStartDate] = useState(
    form.get(keys.startDate) && formatDate(
      parseDate(form.get(keys.startDate), { format: 'internal_date_time', timezone: 'UTC' }),
      { format: 'internal_date' },
    ),
  );

  // UTC date is converted to project timezone for the date picker
  const [startTime, setStartTime] = useState(
    form.get(keys.startDate) && formatDate(
      parseDate(form.get(keys.startDate), { format: 'internal_date_time', timezone: 'UTC' }),
      { format: 'internal_time' },
    ),
  );

  // UTC date is converted to project timezone for the date picker
  const [endDate, setEndDate] = useState(
    form.get(keys.endDate) && formatDate(
      parseDate(form.get(keys.endDate), { format: 'internal_date_time', timezone: 'UTC' }),
      { format: 'internal_date' },
    ),
  );

  // UTC date is converted to project timezone for the date picker
  const [endTime, setEndTime] = useState(
    form.get(keys.endDate) && formatDate(
      parseDate(form.get(keys.endDate), { format: 'internal_date_time', timezone: 'UTC' }),
      { format: 'internal_time' },
    ),
  );

  /** Converts the date to UTC */
  const getUtcDate = (date: string | null, time: string | null) => {
    if (date && time) {
      return formatDate(
        parseDate(`${date} ${time}`),
        { format: 'internal_date_time', timezone: 'UTC' },
      );
    }

    if (date) {
      return formatDate(
        parseDate(date, { format: 'internal_date' }),
        { format: 'internal_date_time', timezone: 'UTC' },
      );
    }

    return null;
  };

  const handleStartDateChange = (startDate: string) => {
    setStartDate(startDate);
    form.set(keys.startDate, getUtcDate(startDate, startTime));
    prefillEndDate(startDate, startTime, endDate, endTime);
  };

  const handleStartTimeChange = (startTime: string) => {
    setStartTime(startTime);
    form.set(keys.startDate, getUtcDate(startDate, startTime));
    prefillEndDate(startDate, startTime, endDate, endTime);
  };

  const handleEndDateChange = (endDate: string) => {
    setEndDate(endDate);
    form.set(keys.endDate, getUtcDate(endDate, endTime));
    prefillEndDate(startDate, startTime, endDate, endTime);
  };

  const handleEndTimeChange = (endTime: string) => {
    setEndTime(endTime);
    form.set(keys.endDate, getUtcDate(endDate, endTime));
    prefillEndDate(startDate, startTime, endDate, endTime);
  };

  const prefillEndDate = (
    startDate: string | null, startTime: string | null, endDate: string | null, endTime: string | null,
  ) => {
    if (!endDate && startDate && startTime && endTime && startTime <= endTime) {
      handleEndDateChange(startDate);
    }
  };

  // If both start and end date start at the start of the day, assume they span whole days.
  const noTime = allowFullDay
    && (startTime === null || startTime === '00:00:00')
    && (endTime === null || endTime === '00:00:00');

  return (
    <UI.FormGrid sc={{ columns: ['1fr', '1fr 1fr'] }}>
      <UI.InputGroup
        sc={{
          valid: !errors[keys.startDate] && !errors[keys.startTime],
          touched: form.getTouched(keys.startDate) || form.getTouched(keys.startTime),
        }}
      >
        <UI.InputLabel htmlFor={`StartDate_${keys.startDate}`}>
          {labels?.startDate || t('start_date')}
          {requireStart && (
            <>
              {' '}
              <UI.RequiredMark />
            </>
          )}
        </UI.InputLabel>
        <UI.FormTable>
          <UI.TBody>
            <UI.TR>
              <UI.TD>
                <UI.DatePicker
                  selected={startDate}
                  onChange={handleStartDateChange}
                  selectsStart
                  endDate={endDate}
                  maxDate={endDate}
                  onBlur={() => form.setTouched(keys.startDate, true)}
                  id={`StartDate_${keys.startDate}`}
                  placeholder={placeholders?.startDate}
                />
              </UI.TD>
              <UI.TD>
                <UI.TimePicker
                  selected={noTime ? null : startTime}
                  onChange={handleStartTimeChange}
                  onBlur={() => form.setTouched(keys.startTime, true)}
                  id={`StartTime_${keys.startTime}`}
                  placeholder={placeholders?.startTime ?? (startDate && !allowFullDay ? '00:00' : null)}
                  aria-label={labels?.startTime || t('start_time')}
                />
              </UI.TD>
            </UI.TR>
          </UI.TBody>
        </UI.FormTable>
        <UI.ErrorMessages attribute={t('start_date')} errors={errors[keys.startDate]} />
        <UI.ErrorMessages attribute={t('start_time')} errors={errors[keys.startTime]} />
      </UI.InputGroup>

      <UI.InputGroup
        sc={{
          valid: !errors[keys.endDate] && !errors[keys.endTime],
          touched: form.getTouched(keys.endDate) || form.getTouched(keys.endTime),
        }}
      >
        <UI.InputLabel htmlFor={`EndDate_${keys.endDate}`}>
          {labels?.endDate || t('end_date')}
          {requireEnd && (
            <>
              {' '}
              <UI.RequiredMark />
            </>
          )}
        </UI.InputLabel>
        <UI.FormTable>
          <UI.TBody>
            <UI.TR>
              <UI.TD>
                <UI.DatePicker
                  selected={endDate}
                  onChange={handleEndDateChange}
                  onBlur={() => form.setTouched(keys.endDate, true)}
                  selectsEnd
                  startDate={startDate}
                  id={`EndDate_${keys.endDate}`}
                  placeholder={placeholders?.endDate}
                />
              </UI.TD>
              <UI.TD>
                <UI.TimePicker
                  selected={noTime ? null : endTime}
                  onChange={handleEndTimeChange}
                  onBlur={() => form.setTouched(keys.endTime, true)}
                  minTime={startDate && (startDate === endDate || !endTime) ? startTime : null}
                  id={`EndTime_${keys.endTime}`}
                  placeholder={placeholders?.endTime ?? (endDate && !allowFullDay ? '00:00' : null)}
                  aria-label={labels?.endTime || t('end_time')}
                />
              </UI.TD>
            </UI.TR>
          </UI.TBody>
        </UI.FormTable>
        <UI.ErrorMessages attribute={t('end_date')} errors={errors[keys.endDate]} />
        <UI.ErrorMessages attribute={t('end_time')} errors={errors[keys.endTime]} />
      </UI.InputGroup>
    </UI.FormGrid>
  );
};

interface DateTimeFormProps {
  form: FormManager;
  errors?: ErrorBag;
  require?: boolean;
  keys?: {
    date: string;
    time: string; // Only used for touch events
  };
  labels?: {
    date: ReactNode;
    time: ReactNode;
  };
  hideLabels?: boolean;
  hideErrors?: boolean;
  minDate?: string;
  maxDate?: string;
  gutter?: GridProps['gutter'];
}

const dateKeys = {
  date: 'date',
  time: 'time',
};

export const DateTimeForm = ({
  form, errors = {}, require = false, keys = dateKeys, labels, hideLabels, hideErrors, minDate, maxDate, gutter = 1,
}: DateTimeFormProps) => {
  const { t } = useTranslation();
  const { formatDate, parseDate } = useLocale();

  // UTC date is converted to project timezone for the date picker
  const [date, setDate] = useState(
    form.get(keys.date) && formatDate(
      parseDate(form.get(keys.date), { format: 'internal_date_time', timezone: 'UTC' }),
      { format: 'internal_date' },
    ),
  );

  // UTC date is converted to project timezone for the date picker
  const [time, setTime] = useState(() => {
    const date = form.get(keys.date);

    if (date) {
      const parsedDate = parseDate(date, { format: 'internal_date_time', timezone: 'UTC' });

      if (parsedDate.getTime() !== startOfDay(parsedDate).getTime()) {
        return formatDate(parsedDate, { format: 'internal_time' });
      }
    }

    return null;
  });

  /** Converts the date to UTC */
  const getUtcDate = (date: string | null, time: string | null) => {
    if (date && time) {
      return formatDate(
        parseDate(`${date} ${time}`),
        { format: 'internal_date_time', timezone: 'UTC' },
      );
    }

    if (date) {
      return formatDate(
        parseDate(date, { format: 'internal_date' }),
        { format: 'internal_date_time', timezone: 'UTC' },
      );
    }

    return null;
  };

  const handleDateChange = (date: string) => {
    setDate(date);
    form.set(keys.date, getUtcDate(date, time));
  };

  const handleTimeChange = (time: string) => {
    setTime(time);
    form.set(keys.date, getUtcDate(date, time));
  };

  const dateLabel = labels?.date ? String(labels.date) : t(keys.date);
  const timeLabel = labels?.date ? String(labels.time) : t(keys.time);

  return (
    <UI.FormGrid sc={{ columns: '1fr 1fr', gutter }}>
      <UI.InputGroup sc={{ valid: !errors[keys.date], touched: form.getTouched(keys.date) }}>
        {!hideLabels && (
          <UI.InputLabel htmlFor={keys.date}>
            {dateLabel}
            {require && (
              <>
                {' '}
                <UI.RequiredMark />
              </>
            )}
          </UI.InputLabel>
        )}
        <UI.DatePicker
          selected={date}
          onChange={handleDateChange}
          onBlur={() => form.setTouched(keys.date, true)}
          minDate={minDate}
          maxDate={maxDate}
          id={keys.date}
        />
        {!hideErrors && (
          <UI.ErrorMessages attribute={dateLabel} errors={errors[keys.date]} />
        )}
      </UI.InputGroup>

      <UI.InputGroup sc={{ valid: !errors[keys.time], touched: form.getTouched(keys.time) }}>
        {!hideLabels && (
          <UI.InputLabel htmlFor={keys.time}>
            {timeLabel}
          </UI.InputLabel>
        )}
        <UI.TimePicker
          selected={time}
          onChange={handleTimeChange}
          onBlur={() => form.setTouched(keys.time, true)}
          placeholder={date
            ? formatDate(parseDate(date, { format: 'internal_date' }), { format: 'input_time' })
            : undefined}
          id={keys.time}
        />
        {!hideErrors && (
          <UI.ErrorMessages attribute={timeLabel} errors={errors[keys.time]} />
        )}
      </UI.InputGroup>
    </UI.FormGrid>
  );
};

export const dateRangeObject = (requireStart: boolean = false, requireEnd: boolean = false) => ({
  start_date: yup.string().nullable().test('required', '', (value) => !requireStart || !!value),
  end_date: yup.string().nullable().when(['start_date'], {
    is: (startDate: string) => !!startDate,
    then: (yup.string() as any).nullable().afterOrEqual(yup.ref('start_date')),
  }).test('required', '', (value) => !requireEnd || !!value),
});

export default DateRangeForm;
